/**
 * Cache Invalidation Orchestrator
 * 
 * Coordinates the invalidation of all cache types when database changes are detected.
 * Manages localStorage, sessionStorage, React Query cache, and other client-side caches.
 * 
 * Phase 5 implementation - production-ready with monitoring, recovery, and configuration
 */

import { QueryClient } from '@tanstack/react-query';
import { invalidateBrandingCache } from './brandingCacheIntegration';
import { 
  invalidateCommunityCache, 
  detectCommunityContext,
  type CommunityContext 
} from './communityAwareCacheInvalidation';
import {
  performSelectiveInvalidation,
  invalidateWithPreset,
  INVALIDATION_PRESETS,
  type SelectiveInvalidationOptions,
  type SelectiveInvalidationResult
} from './selectiveInvalidationService';
import { migrateLegacyKeys, needsMigration } from './namespacedStorageService';
import { 
  trackCacheOperation, 
  collectCacheHealthMetrics 
} from './cacheMonitoringService';
import { 
  selectiveInvalidationWithRecovery, 
  migrationWithRecovery 
} from './cacheRecoveryService';
import { 
  getConfig, 
  isFeatureEnabled, 
  getPerformanceConfig 
} from './cacheConfigurationService';
import { isDevelopment } from '@/lib/utils/environment';

// Known localStorage keys that should be cleared during reset
// Phase 2: Basic implementation, will be enhanced in Phase 4
const KNOWN_CACHE_KEYS = [
  'terrarium_meta_version',
  'terrarium_db_boot_time',
  'terrarium_community_branding_v4',
  'terrarium_community_member_onboarding', // Jotai atomWithStorage key
];

// Session storage keys to clear
const SESSION_CACHE_KEYS = [
  // Add session storage keys here as we discover them
];

/**
 * Interface for cache invalidation results (Phase 5 enhanced with monitoring)
 */
export interface CacheInvalidationResult {
  success: boolean;
  localStorageKeysCleared: number;
  sessionStorageKeysCleared: number;
  reactQueryCacheCleared: boolean;
  brandingInvalidated: boolean;
  communityInvalidated: boolean;
  communityContext?: CommunityContext;
  selectiveInvalidation?: SelectiveInvalidationResult;
  migrationPerformed?: boolean;
  recoveryUsed?: boolean;
  performanceMetrics?: {
    totalDuration: number;
    migrationDuration?: number;
    invalidationDuration?: number;
  };
  healthMetrics?: {
    beforeInvalidation: any;
    afterInvalidation: any;
  };
  errors: string[];
}

/**
 * Clear localStorage cache entries
 */
function clearLocalStorageCache(): { keysCleared: number; errors: string[] } {
  let keysCleared = 0;
  const errors: string[] = [];

  // Clear known keys
  KNOWN_CACHE_KEYS.forEach(key => {
    try {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        keysCleared++;
        
        if (isDevelopment) {
          console.log(`CacheOrchestrator: Cleared localStorage key: ${key}`);
        }
      }
    } catch (error) {
      const errorMsg = `Failed to clear localStorage key ${key}: ${error}`;
      errors.push(errorMsg);
      console.error('CacheOrchestrator:', errorMsg);
    }
  });

  // Phase 2: Basic wildcard clearing for any remaining terrarium keys
  // In Phase 4, this will be more sophisticated with namespacing
  try {
    const allKeys = Object.keys(localStorage);
    const terrariumKeys = allKeys.filter(key => key.startsWith('terrarium_'));
    
    terrariumKeys.forEach(key => {
      if (!KNOWN_CACHE_KEYS.includes(key)) {
        try {
          localStorage.removeItem(key);
          keysCleared++;
          
          if (isDevelopment) {
            console.log(`CacheOrchestrator: Cleared additional localStorage key: ${key}`);
          }
        } catch (error) {
          const errorMsg = `Failed to clear additional localStorage key ${key}: ${error}`;
          errors.push(errorMsg);
          console.error('CacheOrchestrator:', errorMsg);
        }
      }
    });
  } catch (error) {
    errors.push(`Failed to scan localStorage keys: ${error}`);
  }

  return { keysCleared, errors };
}

/**
 * Clear sessionStorage cache entries
 */
function clearSessionStorageCache(): { keysCleared: number; errors: string[] } {
  let keysCleared = 0;
  const errors: string[] = [];

  SESSION_CACHE_KEYS.forEach(key => {
    try {
      if (sessionStorage.getItem(key) !== null) {
        sessionStorage.removeItem(key);
        keysCleared++;
        
        if (isDevelopment) {
          console.log(`CacheOrchestrator: Cleared sessionStorage key: ${key}`);
        }
      }
    } catch (error) {
      const errorMsg = `Failed to clear sessionStorage key ${key}: ${error}`;
      errors.push(errorMsg);
      console.error('CacheOrchestrator:', errorMsg);
    }
  });

  return { keysCleared, errors };
}

/**
 * Clear React Query cache
 */
function clearReactQueryCache(queryClient: QueryClient | null): { success: boolean; error?: string } {
  if (!queryClient) {
    return { success: false, error: 'QueryClient not available' };
  }

  try {
    // Clear all queries and mutations
    queryClient.clear();
    
    if (isDevelopment) {
      console.log('CacheOrchestrator: React Query cache cleared');
    }
    
    return { success: true };
  } catch (error) {
    const errorMsg = `Failed to clear React Query cache: ${error}`;
    console.error('CacheOrchestrator:', errorMsg);
    return { success: false, error: errorMsg };
  }
}

/**
 * Orchestrate complete cache invalidation (Phase 5 production-ready with monitoring and recovery)
 */
export async function orchestrateCacheInvalidation(
  queryClient: QueryClient | null,
  reason: string
): Promise<CacheInvalidationResult> {
  return trackCacheOperation('invalidation', async () => {
    const config = getConfig();
    const startTime = performance.now();
    
    if (config.debugging.enableConsoleLogging) {
      console.log('CacheOrchestrator: Starting Phase 5 production-ready cache invalidation', { 
        reason, 
        featuresEnabled: config.features 
      });
    }

    const result: CacheInvalidationResult = {
      success: false,
      localStorageKeysCleared: 0,
      sessionStorageKeysCleared: 0,
      reactQueryCacheCleared: false,
      brandingInvalidated: false,
      communityInvalidated: false,
      migrationPerformed: false,
      recoveryUsed: false,
      performanceMetrics: {
        totalDuration: 0
      },
      errors: []
    };

    // Collect health metrics before invalidation
    if (isFeatureEnabled('enableMonitoring')) {
      try {
        result.healthMetrics = {
          beforeInvalidation: await collectCacheHealthMetrics(),
          afterInvalidation: null
        };
      } catch (error) {
        result.errors.push(`Health metrics collection failed: ${error}`);
      }
    }

    // Detect current community context for targeted invalidation
    const communityContext = detectCommunityContext();
    result.communityContext = communityContext;

    // Phase 5: Enhanced migration with recovery if enabled
    if (isFeatureEnabled('enableAutomaticMigration') && needsMigration()) {
      const migrationStartTime = performance.now();
      
      try {
        if (config.debugging.enableConsoleLogging) {
          console.log('CacheOrchestrator: Legacy key migration needed - performing migration with recovery');
        }
        
        if (isFeatureEnabled('enableErrorRecovery')) {
          const migrationRecoveryResult = await migrationWithRecovery();
          result.migrationPerformed = migrationRecoveryResult.success;
          result.recoveryUsed = migrationRecoveryResult.strategy !== 'retry' || migrationRecoveryResult.attemptsUsed > 1;
          
          if (migrationRecoveryResult.success && migrationRecoveryResult.result?.data) {
            const migrationData = migrationRecoveryResult.result.data;
            if (config.debugging.enableConsoleLogging) {
              console.log('CacheOrchestrator: Migration with recovery completed', {
                migrated: migrationData.migrated.length,
                errors: migrationData.errors.length,
                recoveryStrategy: migrationRecoveryResult.strategy,
                attempts: migrationRecoveryResult.attemptsUsed
              });
            }
            
            if (migrationData.errors.length > 0) {
              result.errors.push(...migrationData.errors);
            }
          } else {
            result.errors.push(`Migration with recovery failed: ${migrationRecoveryResult.errors.join(', ')}`);
          }
        } else {
          // Fallback to direct migration without recovery
          const migrationResult = await migrateLegacyKeys();
          result.migrationPerformed = migrationResult.success;
          
          if (migrationResult.success && migrationResult.data) {
            if (migrationResult.data.errors.length > 0) {
              result.errors.push(...migrationResult.data.errors);
            }
          } else {
            result.errors.push('Migration failed');
          }
        }
        
        result.performanceMetrics!.migrationDuration = performance.now() - migrationStartTime;
        
      } catch (error) {
        result.errors.push(`Migration error: ${error}`);
        result.performanceMetrics!.migrationDuration = performance.now() - migrationStartTime;
      }
    }

    // Phase 5: Enhanced selective invalidation with recovery
    if (isFeatureEnabled('enableSelectiveInvalidation')) {
      const invalidationStartTime = performance.now();
      const isDbReset = reason.toLowerCase().includes('database reset') || 
                       reason.toLowerCase().includes('meta-version change');
      
      try {
        const presetName = isDbReset ? 'databaseReset' : 'communitySwitch';
        
        if (isFeatureEnabled('enableErrorRecovery')) {
          const invalidationRecoveryResult = await selectiveInvalidationWithRecovery(
            queryClient,
            presetName,
            { communityContext }
          );
          
          if (invalidationRecoveryResult.success && invalidationRecoveryResult.result) {
            const selectiveResult = invalidationRecoveryResult.result;
            result.selectiveInvalidation = selectiveResult;
            result.localStorageKeysCleared = selectiveResult.storageKeysCleared.length;
            result.reactQueryCacheCleared = selectiveResult.queryKeysCleared.length > 0;
            result.brandingInvalidated = selectiveResult.brandingInvalidated;
            result.recoveryUsed = result.recoveryUsed || 
              (invalidationRecoveryResult.strategy !== 'retry' || invalidationRecoveryResult.attemptsUsed > 1);
            
            if (!selectiveResult.success) {
              result.errors.push(...selectiveResult.errors);
            }
            
            if (config.debugging.enableConsoleLogging) {
              console.log('CacheOrchestrator: Selective invalidation with recovery completed', {
                preset: presetName,
                keysCleared: selectiveResult.storageKeysCleared.length,
                keysPreserved: selectiveResult.preservedKeys.length,
                recoveryStrategy: invalidationRecoveryResult.strategy,
                attempts: invalidationRecoveryResult.attemptsUsed,
                duration: selectiveResult.durationMs
              });
            }
          } else {
            result.errors.push(`Selective invalidation with recovery failed: ${invalidationRecoveryResult.errors.join(', ')}`);
            
            // Fallback to legacy clearing
            const localStorageResult = clearLocalStorageCache();
            result.localStorageKeysCleared = localStorageResult.keysCleared;
            result.errors.push(...localStorageResult.errors);

            const reactQueryResult = clearReactQueryCache(queryClient);
            result.reactQueryCacheCleared = reactQueryResult.success;
            if (reactQueryResult.error) {
              result.errors.push(reactQueryResult.error);
            }
          }
        } else {
          // Direct selective invalidation without recovery
          const selectiveResult = await invalidateWithPreset(
            queryClient,
            presetName,
            { communityContext }
          );
          
          result.selectiveInvalidation = selectiveResult;
          result.localStorageKeysCleared = selectiveResult.storageKeysCleared.length;
          result.reactQueryCacheCleared = selectiveResult.queryKeysCleared.length > 0;
          result.brandingInvalidated = selectiveResult.brandingInvalidated;
          
          if (!selectiveResult.success) {
            result.errors.push(...selectiveResult.errors);
          }
        }
        
        result.performanceMetrics!.invalidationDuration = performance.now() - invalidationStartTime;
        
      } catch (error) {
        result.errors.push(`Selective invalidation error: ${error}`);
        result.performanceMetrics!.invalidationDuration = performance.now() - invalidationStartTime;
      }
    } else {
      // Fallback to legacy clearing when selective invalidation is disabled
      const localStorageResult = clearLocalStorageCache();
      result.localStorageKeysCleared = localStorageResult.keysCleared;
      result.errors.push(...localStorageResult.errors);

      const reactQueryResult = clearReactQueryCache(queryClient);
      result.reactQueryCacheCleared = reactQueryResult.success;
      if (reactQueryResult.error) {
        result.errors.push(reactQueryResult.error);
      }
    }

    // Clear sessionStorage (not yet part of selective invalidation)
    const sessionStorageResult = clearSessionStorageCache();
    result.sessionStorageKeysCleared = sessionStorageResult.keysCleared;
    result.errors.push(...sessionStorageResult.errors);

    // Legacy Phase 3 community invalidation as fallback
    if (isFeatureEnabled('enableCommunityAwareness') && queryClient && !result.communityInvalidated) {
      try {
        const communityResult = await invalidateCommunityCache(
          queryClient,
          communityContext,
          {
            invalidateBranding: false, // Already handled by selective invalidation
            invalidateReactQuery: false, // Already handled by selective invalidation
            invalidateLocalStorage: false, // Already handled by selective invalidation
            preserveAuth: true,
            preserveUserPreferences: true,
          },
          `Orchestrated community invalidation: ${reason}`
        );
        result.communityInvalidated = communityResult.success;
        if (!communityResult.success) {
          result.errors.push(...communityResult.errors);
        }
      } catch (error) {
        result.errors.push(`Community invalidation failed: ${error}`);
      }
    }

    // Collect health metrics after invalidation
    if (isFeatureEnabled('enableMonitoring') && result.healthMetrics) {
      try {
        result.healthMetrics.afterInvalidation = await collectCacheHealthMetrics();
      } catch (error) {
        result.errors.push(`Post-invalidation health metrics failed: ${error}`);
      }
    }

    // Determine overall success
    result.success = result.errors.length === 0;
    result.performanceMetrics!.totalDuration = performance.now() - startTime;

    if (config.debugging.enableConsoleLogging) {
      console.log('CacheOrchestrator: Phase 5 cache invalidation completed', {
        success: result.success,
        localStorageKeysCleared: result.localStorageKeysCleared,
        migrationPerformed: result.migrationPerformed,
        recoveryUsed: result.recoveryUsed,
        selectiveInvalidation: result.selectiveInvalidation ? 'enabled' : 'disabled',
        durationMs: result.performanceMetrics?.totalDuration?.toFixed(2),
        errors: result.errors.length,
        reason
      });
    }

    return result;
  }, { operation: 'orchestrateCacheInvalidation', reason });
}

/**
 * Partial cache invalidation for specific cache types
 * Useful for more granular cache management
 */
export interface PartialInvalidationOptions {
  localStorage?: boolean;
  sessionStorage?: boolean;
  reactQuery?: boolean;
  customKeys?: string[];
}

export async function orchestratePartialCacheInvalidation(
  options: PartialInvalidationOptions,
  queryClient: QueryClient | null,
  reason: string
): Promise<CacheInvalidationResult> {
  const startTime = performance.now();
  
  if (isDevelopment) {
    console.log('CacheOrchestrator: Starting partial cache invalidation', { options, reason });
  }

  const result: CacheInvalidationResult = {
    success: false,
    localStorageKeysCleared: 0,
    sessionStorageKeysCleared: 0,
    reactQueryCacheCleared: false,
    errors: []
  };

  // Clear localStorage if requested
  if (options.localStorage) {
    const localStorageResult = clearLocalStorageCache();
    result.localStorageKeysCleared = localStorageResult.keysCleared;
    result.errors.push(...localStorageResult.errors);
  }

  // Clear sessionStorage if requested
  if (options.sessionStorage) {
    const sessionStorageResult = clearSessionStorageCache();
    result.sessionStorageKeysCleared = sessionStorageResult.keysCleared;
    result.errors.push(...sessionStorageResult.errors);
  }

  // Clear React Query cache if requested
  if (options.reactQuery) {
    const reactQueryResult = clearReactQueryCache(queryClient);
    result.reactQueryCacheCleared = reactQueryResult.success;
    if (reactQueryResult.error) {
      result.errors.push(reactQueryResult.error);
    }
  }

  // Clear custom keys if provided
  if (options.customKeys && options.customKeys.length > 0) {
    options.customKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        result.localStorageKeysCleared++;
      } catch (error) {
        result.errors.push(`Failed to clear custom key ${key}: ${error}`);
      }
    });
  }

  // Determine overall success
  result.success = result.errors.length === 0;

  const duration = performance.now() - startTime;
  
  if (isDevelopment) {
    console.log('CacheOrchestrator: Partial cache invalidation completed', {
      ...result,
      durationMs: duration.toFixed(2),
      reason
    });
  }

  return result;
}